import { useEffect, useState } from "react";
import { Clock, Wifi } from "lucide-react";
import type { PaymentItemKind } from "@fiket/booth-schema";
import { useRouter } from "../../store/router";
import { useBoothConfig } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";
import { QRCode2D } from "../PrintShare/QRCode";
import { usePayment } from "./usePayment";
import { PAYMENT_EXIT_ROUTE, afterPaidRoute, videoCharged } from "./paymentFlow";
import { formatKrw, formatRemaining, isUrgent, pollBanner, remainingMs } from "./paymentViewLogic";
import { trackVisitEnd } from "../../telemetry/activity";

/**
 * STAGE · Payment (BOOTH-61/74/80, 캔버스 10) — 결제가 걸리는 부스에서 영상 생성(또는 사진 수령) 직전. 항목(사진/영상)은 서버가 스위치별로 정한다.
 * 좌: 내역 표(사진 인화 / 움직이는 화보 / 합계 — 금액은 **생성 응답**으로만, F6) + 안내.
 * 우: QR(서버 payUrl = 손님 폰 결제 페이지) + 킥커 + 수단 + 남은 시간 + 배너 + Wi-Fi 안내. CTA 는 취소 하나.
 * paid → videoGenerate | printShare, 취소·만료·서버 종결 → review(+ 한 줄 안내). 생성 실패는 코드별로 건너뛰거나 에러 화면.
 * 진행 표시는 5단계(사진 받기) 그대로 — 결제는 별도 단계가 아니다 (스펙 §3).
 */
export function PaymentView() {
  const go = useRouter((s) => s.go);
  const withVideo = useRouter((s) => s.paymentWithVideo);
  const setPaymentPaid = useRouter((s) => s.setPaymentPaid);
  const setPaymentExit = useRouter((s) => s.setPaymentExit);
  const clearPayment = useRouter((s) => s.clearPayment);
  const resetSession = useRouter((s) => s.resetSession);
  const boothId = useBoothConfig((s) => s.config?.booth.id ?? null);
  const refresh = useBoothConfig((s) => s.refresh);
  const { t, language } = useI18n();
  const visitId = useRouter((s) => s.visitId);
  const [attempt, setAttempt] = useState(0);
  const { state, cancel, cancelling } = usePayment({ boothId, withVideo, language, attempt, visitId });

  // 결과 라우팅은 effect 에서 — 렌더 중 go() 금지
  useEffect(() => {
    switch (state.kind) {
      case "paid":
        setPaymentPaid(state.paymentId, state.items);
        go(afterPaidRoute(withVideo));
        break;
      case "closed":
        clearPayment();
        setPaymentExit(state.reason);
        go(PAYMENT_EXIT_ROUTE);
        break;
      case "skip":
        // 서버 판정과 config 가 어긋남(F5·F1) — 다음 손님부터 맞게 보이도록 조용히 재조회
        void refresh();
        clearPayment();
        go(state.route);
        break;
      default:
        break;
    }
  }, [state, withVideo, go, setPaymentPaid, setPaymentExit, clearPayment, refresh]);

  if (state.kind === "error") {
    const unavailable = state.code === "PAYMENT_UNAVAILABLE";
    return (
      <div className="view view--pay">
        <div className="view__body center pay-error" role="alert">
          <h1><Lines text={unavailable ? t.payment.unavailable : t.payment.createError} /></h1>
          <div className="actions actions--pair pay-error__actions">
            <Button variant="secondary" onClick={() => { trackVisitEnd("home"); resetSession(); }}>{t.printShare.home}</Button>
            {/* 결제 서비스 자체가 없을 땐(F2, Toss 키 미설정) 다시 시도가 의미 없다 — 직원 호출만 */}
            {unavailable ? null : <Button onClick={() => setAttempt((a) => a + 1)}>{t.printShare.retry}</Button>}
          </div>
        </div>
      </div>
    );
  }

  const pending = state.kind === "pending" ? state : null;
  const items = pending?.view.items ?? null;
  const amountKrw = pending?.view.amountKrw ?? null;
  const remaining = pending ? remainingMs(pending.startedAtMs, pending.nowMs) : null;
  const banner = pending
    ? pollBanner({ status: pending.status, nowMs: pending.nowMs, lastOkAtMs: pending.lastOkAtMs, startedAtMs: pending.startedAtMs })
    : "none";
  const itemLabel: Record<PaymentItemKind, string> = { photo: t.payment.itemPhoto, video: t.payment.itemVideo };

  return (
    <div className="view view--pay view--stackable">
      <h1 className="view__title">{t.payment.title}</h1>

      <div className="view__body pay">
        {/* 좌: 내역 — 금액은 생성 응답(items 스냅숏) 으로만 그린다 */}
        <section className="pay__items" aria-label={t.payment.title}>
          <ul className="pay__list">
            {items
              ? items.map((i) => (
                  <li key={i.kind}><span>{itemLabel[i.kind]}</span><b>{formatKrw(i.amountKrw, language)}</b></li>
                ))
              : <li className="pay__list--skeleton" aria-hidden><span /><b /></li>}
          </ul>
          <div className="pay__total">
            <span>{t.payment.total}</span>
            <b>{amountKrw !== null ? formatKrw(amountKrw, language) : "—"}</b>
          </div>
          {/* 안내는 실제 항목 기준 — 사진 ON + 영상 결제 OFF 부스는 영상 생성 직전이어도 사진만 결제한다 */}
          <p className="pay__notice"><Lines text={(items ? videoCharged(items) : withVideo) ? t.payment.noticeVideo : t.payment.noticePhoto} /></p>
        </section>

        {/* 우: QR + 안내 (받기 화면 .deliver__side 와 같은 리듬) */}
        <div className="deliver__side pay__side">
          <div className="qr-box" role="img" aria-label={t.payment.qrHeading}>
            {pending ? <QRCode2D value={pending.view.payUrl} size={432} /> : <div className="qr-skeleton" />}
          </div>
          <span className="kicker">{t.payment.kicker}</span>
          <h2>{t.payment.qrHeading}</h2>
          <p>{t.payment.qrHint}</p>
          <p className="pay__methods">{t.payment.methods}</p>
          <p className={isUrgent(remaining ?? Infinity) ? "pay__timer pay__timer--urgent" : "pay__timer"} aria-live="polite">
            <Clock size={22} aria-hidden />
            <span>{pending ? t.payment.remaining(formatRemaining(remaining ?? 0)) : "—"}</span>
          </p>
          {banner !== "none" ? (
            <p className="pay__banner note" role="status">
              {banner === "connection" ? t.payment.connectionLost : t.payment.failedHint}
            </p>
          ) : null}
          <p className="privacy"><Wifi size={20} aria-hidden /><span>{t.payment.wifiHint}</span></p>
        </div>
      </div>

      <div className="view__cta actions">
        <Button variant="secondary" disabled={!pending || cancelling} onClick={cancel}>{t.payment.cancel}</Button>
      </div>
    </div>
  );
}
